import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdCloudUpload, MdDownload, MdHistory } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import {
    fetchManufacturerImports,
    fetchManufacturerExports,
    createManufacturerImport,
    createManufacturerExport,
} from '../../../../services/Http/ManufacturerApi';

const importTypes = ['sku', 'warranty', 'distributor'];

const ManufacturerImportExport = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeTab, setActiveTab] = useState('imports');
    const [imports, setImports] = useState([]);
    const [exports, setExports] = useState([]);
    const [importForm, setImportForm] = useState({
        import_type: 'sku',
        source_name: '',
        source_url: '',
    });
    const [exportForm, setExportForm] = useState({
        export_type: 'sku',
        file_name: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const [importsResponse, exportsResponse] = await Promise.all([
                fetchManufacturerImports(),
                fetchManufacturerExports(),
            ]);
            setImports(importsResponse.data || []);
            setExports(exportsResponse.data || []);
        } catch (err) {
            console.error(err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleImportSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        try {
            await createManufacturerImport(importForm);
            toast.success(t('ManufacturerImportExport.toast.importCreated'));
            setImportForm({ ...importForm, source_name: '', source_url: '' });
            await loadData();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerImportExport.toast.error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleExportSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        try {
            await createManufacturerExport(exportForm);
            toast.success(t('ManufacturerImportExport.toast.exportCreated'));
            setExportForm({ ...exportForm, file_name: '' });
            await loadData();
        } catch (err) {
            console.error(err);
            toast.error(t('ManufacturerImportExport.toast.error'));
        } finally {
            setSubmitting(false);
        }
    };

    const latestImportsByType = useMemo(() => {
        const grouped = {};
        imports.forEach((item) => {
            if (!grouped[item.import_type] || new Date(item.created_at) > new Date(grouped[item.import_type].created_at)) {
                grouped[item.import_type] = item;
            }
        });
        return grouped;
    }, [imports]);

    const latestExportsByType = useMemo(() => {
        const grouped = {};
        exports.forEach((item) => {
            if (!grouped[item.export_type] || new Date(item.created_at) > new Date(grouped[item.export_type].created_at)) {
                grouped[item.export_type] = item;
            }
        });
        return grouped;
    }, [exports]);

    if (loading) {
        return <LoadingMessage>{t('ManufacturerImportExport.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('ManufacturerImportExport.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <TitleGroup>
                    <PageTitle>{t('ManufacturerImportExport.title')}</PageTitle>
                    <Subtitle>{t('ManufacturerImportExport.subtitle')}</Subtitle>
                </TitleGroup>
                <Tabs>
                    <TabButton type="button" $active={activeTab === 'imports'} onClick={() => setActiveTab('imports')}>
                        <MdCloudUpload /> {t('ManufacturerImportExport.tabs.imports')}
                    </TabButton>
                    <TabButton type="button" $active={activeTab === 'exports'} onClick={() => setActiveTab('exports')}>
                        <MdDownload /> {t('ManufacturerImportExport.tabs.exports')}
                    </TabButton>
                </Tabs>
            </PageHeader>
            <PageContent>
                {activeTab === 'imports' ? (
                    <>
                        <Section>
                            <SectionHeader>
                                <MdCloudUpload />
                                <div>
                                    <SectionTitle>{t('ManufacturerImportExport.imports.requestTitle')}</SectionTitle>
                                    <SectionSubtitle>{t('ManufacturerImportExport.imports.requestSubtitle')}</SectionSubtitle>
                                </div>
                            </SectionHeader>
                            <Form onSubmit={handleImportSubmit}>
                                <label>{t('ManufacturerImportExport.form.type')}</label>
                                <select
                                    value={importForm.import_type}
                                    onChange={(event) => setImportForm((prev) => ({ ...prev, import_type: event.target.value }))}
                                >
                                    {importTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {t(`ManufacturerImportExport.types.${type}`)}
                                        </option>
                                    ))}
                                </select>
                                <label>{t('ManufacturerImportExport.form.sourceName')}</label>
                                <input
                                    type="text"
                                    value={importForm.source_name}
                                    onChange={(event) => setImportForm((prev) => ({ ...prev, source_name: event.target.value }))}
                                />
                                <label>{t('ManufacturerImportExport.form.sourceUrl')}</label>
                                <input
                                    type="url"
                                    value={importForm.source_url}
                                    onChange={(event) => setImportForm((prev) => ({ ...prev, source_url: event.target.value }))}
                                />
                                <PrimaryButton type="submit" disabled={submitting}>
                                    {submitting ? t('ManufacturerImportExport.actions.submitting') : t('ManufacturerImportExport.actions.startImport')}
                                </PrimaryButton>
                            </Form>
                        </Section>

                        <HistorySection>
                            <HistoryHeader>
                                <MdHistory /> {t('ManufacturerImportExport.history.imports')}
                            </HistoryHeader>
                            <HistoryTable>
                                <thead>
                                    <tr>
                                        <th>{t('ManufacturerImportExport.table.type')}</th>
                                        <th>{t('ManufacturerImportExport.table.source')}</th>
                                        <th>{t('ManufacturerImportExport.table.status')}</th>
                                        <th>{t('ManufacturerImportExport.table.progress')}</th>
                                        <th>{t('ManufacturerImportExport.table.date')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {imports.slice(0, 20).map((item) => (
                                        <tr key={item.id}>
                                            <td>{t(`ManufacturerImportExport.types.${item.import_type}`)}</td>
                                            <td>{item.source_name || '—'}</td>
                                            <td>{t(`ManufacturerImportExport.status.${item.status}`)}</td>
                                            <td>
                                                {item.processed_rows ?? 0}/{item.total_rows ?? '—'}
                                            </td>
                                            <td>{new Date(item.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </HistoryTable>
                        </HistorySection>
                    </>
                ) : (
                    <>
                        <Section>
                            <SectionHeader>
                                <MdDownload />
                                <div>
                                    <SectionTitle>{t('ManufacturerImportExport.exports.requestTitle')}</SectionTitle>
                                    <SectionSubtitle>{t('ManufacturerImportExport.exports.requestSubtitle')}</SectionSubtitle>
                                </div>
                            </SectionHeader>
                            <Form onSubmit={handleExportSubmit}>
                                <label>{t('ManufacturerImportExport.form.type')}</label>
                                <select
                                    value={exportForm.export_type}
                                    onChange={(event) => setExportForm((prev) => ({ ...prev, export_type: event.target.value }))}
                                >
                                    {importTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {t(`ManufacturerImportExport.types.${type}`)}
                                        </option>
                                    ))}
                                </select>
                                <label>{t('ManufacturerImportExport.form.fileName')}</label>
                                <input
                                    type="text"
                                    value={exportForm.file_name}
                                    onChange={(event) => setExportForm((prev) => ({ ...prev, file_name: event.target.value }))}
                                    placeholder="export.csv"
                                />
                                <PrimaryButton type="submit" disabled={submitting}>
                                    {submitting ? t('ManufacturerImportExport.actions.submitting') : t('ManufacturerImportExport.actions.startExport')}
                                </PrimaryButton>
                            </Form>
                        </Section>

                        <HistorySection>
                            <HistoryHeader>
                                <MdHistory /> {t('ManufacturerImportExport.history.exports')}
                            </HistoryHeader>
                            <HistoryTable>
                                <thead>
                                    <tr>
                                        <th>{t('ManufacturerImportExport.table.type')}</th>
                                        <th>{t('ManufacturerImportExport.table.status')}</th>
                                        <th>{t('ManufacturerImportExport.table.file')}</th>
                                        <th>{t('ManufacturerImportExport.table.date')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {exports.slice(0, 20).map((item) => (
                                        <tr key={item.id}>
                                            <td>{t(`ManufacturerImportExport.types.${item.export_type}`)}</td>
                                            <td>{t(`ManufacturerImportExport.status.${item.status}`)}</td>
                                            <td>
                                                {item.file_url ? (
                                                    <a href={item.file_url} target="_blank" rel="noreferrer">
                                                        {item.file_name || t('ManufacturerImportExport.table.download')}
                                                    </a>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td>{new Date(item.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </HistoryTable>
                        </HistorySection>
                    </>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerImportExport;

const TitleGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Tabs = styled.div`
    display: inline-flex;
    gap: 0.75rem;
`;

const TabButton = styled.button`
    border: none;
    background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 0.1)' : 'transparent')};
    color: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 1)' : '#555')};
    border-radius: 999px;
    padding: 0.625rem 1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
`;

const Section = styled.section`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    svg {
        font-size: 1.4rem;
        color: rgba(130, 22, 146, 1);
    }
`;

const SectionTitle = styled.h3`
    margin: 0;
`;

const SectionSubtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    input,
    select {
        border-radius: 8px;
        border: 1px solid rgba(130, 22, 146, 0.2);
        padding: 0.625rem;
    }
`;

const PrimaryButton = styled.button`
    border: none;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border-radius: 8px;
    padding: 0.625rem 1rem;
    font-weight: 600;
    cursor: pointer;
`;

const HistorySection = styled.section`
    border: 1px solid rgba(130, 22, 146, 0.1);
    border-radius: 12px;
    padding: 1rem;
`;

const HistoryHeader = styled.h4`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
`;

const HistoryTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 0.625rem;
        border-bottom: 1px solid rgba(130, 22, 146, 0.1);
        text-align: left;
    }
`;
